import React, { useState, useEffect, useRef, useLayoutEffect } from "react";
import moment from "moment";
import style from "./common.module.scss";
import cn from "classnames";
import { useTranslation } from "react-i18next";
import CloseIconNew from "../../../../shared/icons/CloseIconNew";
import { useSelector } from "react-redux";
import { Expeditor, User } from "../../../../../services";

const PartnerChat = ({
  partnerId,
  onClose,
  isMarketplaceTab = false,
  onInvitationCreated,
  isChatHub = false,
}) => {
  const { t } = useTranslation();
  const { user } = useSelector((state) => state);

  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [partnerName, setPartnerName] = useState("");

  const textareaRef = useRef(null);
  const messagesContainerRef = useRef(null);

  useLayoutEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (!partnerId) return;

    setLoading(true);

    Expeditor.getPartnerChats(partnerId)
      .then((res) => res.data)
      .then((data) => {
        const sortedMessages = data?.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
        setMessages(sortedMessages);
      })
      .catch((error) => {
        requestErrorDisplay(error);
      })
      .finally(() => {
        setLoading(false);
      });

    if (!isChatHub) return;

    User.getUsersByCompany(partnerId)
      .then((res) => {
        setPartnerName(res.data?.name || "");
      })
      .catch((error) => {
        requestErrorDisplay(error);
      });
  }, [partnerId]);

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;

    try {
      await Expeditor.sendPartnerChatMessage(partnerId, newMessage);

      if (isMarketplaceTab && messages.length === 0) {
        const invitationResponse = await Expeditor.invitePartner("OpenChat", partnerId);
        if (onInvitationCreated && invitationResponse?.data?.id) {
          onInvitationCreated(invitationResponse.data.id);
        }
      }

      const response = await Expeditor.getPartnerChats(partnerId);
      const sortedMessages = response.data?.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
      setMessages(sortedMessages);
      setNewMessage("");

      if (textareaRef.current) {
        textareaRef.current.style.height = "auto";
      }
    } catch (error) {
      requestErrorDisplay(error);
    }
  };

  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  };

  const formatDateSeparator = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();

    const messageYear = date.getFullYear();
    const currentYear = now.getFullYear();

    if (messageYear === currentYear) {
      return date.toLocaleDateString("ru-RU", {
        month: "long",
        day: "numeric",
      });
    } else {
      return date.toLocaleDateString("ru-RU", {
        month: "long",
        day: "numeric",
        year: "numeric",
      });
    }
  };

  const formatMessageTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString("ru-RU", {
      hour: "numeric",
      minute: "2-digit",
      hour12: false,
    });
  };

  const isSameDay = (date1, date2) => {
    const d1 = new Date(date1);
    const d2 = new Date(date2);
    return d1.toDateString() === d2.toDateString();
  };

  const groupMessagesByDate = (messages) => {
    const grouped = [];
    let currentGroup = null;

    messages.forEach((message, index) => {
      const isNewDay = index === 0 || !isSameDay(message.createdAt, messages[index - 1].createdAt);

      if (isNewDay) {
        currentGroup = {
          date: message.createdAt,
          messages: [message],
        };
        grouped.push(currentGroup);
      } else {
        currentGroup.messages.push(message);
      }
    });

    return grouped;
  };

  const isCurrentUser = (message) => {
    return message.type === user?.type;
  };

  const renderMessages = () => {
    const groupedMessages = groupMessagesByDate(messages);

    return groupedMessages.map((group, groupIndex) => (
      <div key={groupIndex} className={style.messages_list}>
        <div className={style.date_separator}>
          <span className={style.date_text}>{formatDateSeparator(group.date)}</span>
        </div>

        {group.messages.map((message) => {
          const companyName =
            message.type === "Client" ? message.clientCompanyName : message.expeditorCompanyName;

          return (
            <div
              key={message.id}
              className={`${style.message_wrapper} ${
                isCurrentUser(message) ? style.message_right : style.message_left
              }`}
            >
              <div
                className={`${style.message_bubble} ${
                  isCurrentUser(message) ? style.message_user : style.message_other
                }`}
              >
                <div className={style.message_header}>
                  <span className={style.author_name}>
                    {message.authorName}
                    <span className={style.author_type}>({companyName})</span>
                  </span>
                </div>
                <p className={style.message_text}>{message.message}</p>
                <div className={style.message_time}>{formatMessageTime(message.createdAt)}</div>
              </div>
            </div>
          );
        })}
      </div>
    ));
  };

  return (
    <div className={style.chat_container}>
      <div className={cn(style.chat_header, style.driver_header)}>
        <div className={style.header_top}>
          <h2 className={style.chat_title}>{t("orderChat.chatPartner")}</h2>
          <CloseIconNew onClick={onClose} style={{ marginLeft: "auto" }} />
        </div>
        {isChatHub && partnerName && (
          <div className={style.header_bottom}>
            <span className={style.author_name}>{partnerName}</span>
          </div>
        )}
      </div>

      {loading ? (
        <div className={style.loading_container}>
          <div className={style.loader}></div>
        </div>
      ) : (
        <div className={style.history_main} ref={messagesContainerRef}>
          {messages.length === 0 ? (
            <div className={style.empty_chat}>
              <div className={style.empty_icon}>
                <svg
                  width="48"
                  height="48"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M8 12H8.01M12 12H12.01M16 12H16.01M21 12C21 16.418 16.97 20 12 20C10.5286 20 9.14789 19.7119 7.94153 19.1919L3 20L4.39499 16.28C3.51586 15.042 3 13.574 3 12C3 7.582 7.03 4 12 4C16.97 4 21 7.582 21 12Z"
                    stroke="#9CA3AF"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <div className={style.empty_text}>{t("orderChat.noMessages")}</div>
            </div>
          ) : (
            <div className={style.messages_list}>{renderMessages()}</div>
          )}
        </div>
      )}

      <div className={style.input_area}>
        <div className={style.input_container}>
          <textarea
            ref={textareaRef}
            value={newMessage}
            onChange={(e) => {
              setNewMessage(e.target.value);
              e.target.style.height = "auto";
              e.target.style.height = `${e.target.scrollHeight}px`;
            }}
            placeholder={t("orderChat.placeholder")}
            className={style.message_input}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSendMessage();
              }
            }}
            rows={1}
          />

          <button onClick={handleSendMessage} disabled={!newMessage.trim()} className={style.send_button}>
            <span className={style.button_text}>{t("orderChat.sendButton")}</span>
            <svg
              className={style.send_icon}
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M22 2L11 13"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M22 2L15 22L11 13L2 9L22 2Z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default PartnerChat;
